import { Loader } from '@/components/ai-elements/loader'
import { SecureMarkdown } from '@/components/ai-elements/secure-markdown'
import { Shimmer } from '@/components/ai-elements/shimmer'
import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { FocusAttachment } from '@/features/global-chat/focus-attachment'
import type { GlobalChatCommand } from '@/features/global-chat/global-chat-state'
import { useCurrentFocus } from '@/features/global-chat/use-current-focus'
import { useGlobalChat } from '@/features/global-chat/use-global-chat'
import { cn } from '@/lib/utils'
import { Jobibot, Tooltip } from '@/ui/rapid-ui'
import { AnimateChangeInHeight } from '@/ui/rapid-ui/animation/height-animation'
import { logger } from '@/ui/utilities/instrumentation/logger'
import { InformationCircleIcon } from '@heroicons/react/24/outline'
import { conversationIndexQueryKey, useConversationDestroy, useConversationIndex } from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from '@tanstack/react-router'
import { getToolName, isStaticToolUIPart, isTextUIPart } from 'ai'
import type { UIMessage } from 'ai'
import { formatDistanceToNow } from 'date-fns'
import { ArrowLeft, ExternalLink, Maximize2, Minimize2, Pencil, Trash2, X } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { v4 as uuidv4 } from 'uuid'

type ActiveConversation = {
	id: string
	title?: string
}

export function GlobalChat() {
	const { t } = useTranslation('common')
	const [isOpen, setIsOpen] = useState(false)
	const [isExpanded, setIsExpanded] = useState(false)
	const [active, setActive] = useState<ActiveConversation | null>(null)
	const [prefillText, setPrefillText] = useState<string | undefined>()

	const { data: conversations } = useConversationIndex({ staleTime: 30_000, enabled: isOpen })

	useEffect(() => {
		if (!active?.id || active.title) { return }
		const found = conversations?.find((c) => c.id === active.id)
		if (found?.title) {
			setActive((prev) => prev ? { ...prev, title: found.title } : prev)
		}
	}, [conversations, active?.id, active?.title])

	useEffect(() => {
		const handler = (e: Event) => {
			const { conversationId, prefillText: text } = (e as CustomEvent<GlobalChatCommand>).detail
			setActive({ id: conversationId })
			setPrefillText(text)
			setIsOpen(true)
		}
		window.addEventListener('jobibot:open', handler)
		return () => window.removeEventListener('jobibot:open', handler)
	}, [])

	const handleNewConversation = useCallback(() => {
		setActive({ id: uuidv4() })
	}, [])

	const handleOpen = useCallback(() => {
		setIsOpen(true)
		logger.track('Jobibot: Opened')
	}, [])

	const handleClose = useCallback(() => {
		setIsOpen(false)
	}, [])

	return (
		<>
			{isOpen
				? (
					<div
						className={cn(
							'fixed bottom-16 right-4 z-50 flex flex-col rounded-xl border border-border bg-background shadow-2xl overflow-hidden transition-[width,height] duration-200',
							isExpanded
								? 'w-[60vw] h-[85vh]'
								: 'w-80 sm:w-96 h-[520px]',
						)}
					>
						{active
							? (
								<ChatPanel
									conversationId={active.id}
									title={active.title}
									prefillText={prefillText}
									isExpanded={isExpanded}
									onExpand={() => setIsExpanded((v) => !v)}
									onBack={() => setActive(null)}
									onClose={handleClose}
									onPrefillSent={() => setPrefillText(undefined)}
								/>
							)
							: (
								<ThreadList
									isExpanded={isExpanded}
									onExpand={() => setIsExpanded((v) => !v)}
									onSelect={(id, meta) => setActive({ id, ...meta })}
									onNew={handleNewConversation}
									onClose={handleClose}
								/>
							)}
					</div>
				)
				: null}

			<Button
				onClick={handleOpen}
				className={cn(
					'fixed bottom-16 right-4 z-50 size-12 rounded-full shadow-lg p-0 bg-background border border-border hover:bg-muted transition-colors',
					isOpen && 'opacity-0 pointer-events-none',
				)}
				aria-label={t('jobibot.openLabel')}
			>
				<Jobibot size={22} />
			</Button>
		</>
	)
}

function ThreadList({
	isExpanded,
	onExpand,
	onSelect,
	onNew,
	onClose,
}: {
	isExpanded: boolean
	onExpand: () => void
	onSelect: (id: string, meta: Omit<ActiveConversation, 'id'>) => void
	onNew: () => void
	onClose: () => void
}) {
	const { t } = useTranslation('common')
	const { data: conversations, isLoading } = useConversationIndex({ staleTime: 30_000, refetchOnMount: 'always' })
	const queryClient = useQueryClient()
	const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)
	const { mutate: deleteConversation, isPending: isDeleting } = useConversationDestroy({
		onSuccess: () => {
			setPendingDeleteId(null)
			void queryClient.invalidateQueries({ queryKey: conversationIndexQueryKey() })
		},
	})

	return (
		<>
			<div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
				<div className="flex items-center gap-2">
					<Jobibot size={20} />
					<span className="font-semibold text-sm">Jobibot</span>
					<Tooltip content={t('jobibot.betaTooltip')}>
						<span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-violet-100 text-violet-700 rounded cursor-help">
							{t('common.beta')}
							<InformationCircleIcon className="w-3 h-3" />
						</span>
					</Tooltip>
				</div>
				<div className="flex items-center gap-1">
					<Button
						variant="ghost"
						size="icon"
						className="h-7 w-7"
						onClick={onNew}
						title={t('jobibot.newConversation')}
					>
						<Pencil className="h-3.5 w-3.5" />
					</Button>
					<Button
						variant="ghost"
						size="icon"
						className="h-7 w-7"
						onClick={onExpand}
						title={isExpanded ? t('jobibot.shrink') : t('jobibot.expand')}
					>
						{isExpanded ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
					</Button>
					<Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}>
						<X className="h-4 w-4" />
					</Button>
				</div>
			</div>

			<div className="flex-1 overflow-y-auto min-h-0">
				{isLoading
					? (
						<div className="flex items-center justify-center py-10">
							<Loader />
						</div>
					)
					: !conversations?.length
					? (
						<div className="flex flex-col items-center justify-center py-10 gap-3 px-4 text-center">
							<Jobibot size={32} />
							<p className="text-sm text-muted-foreground">{t('jobibot.noConversations')}</p>
							<Button
								size="sm"
								onClick={onNew}
								className="bg-linear-to-br from-indigo-700 via-indigo-600 via-40% to-brand-600 text-white border-0"
							>
								{t('jobibot.startChatting')}
							</Button>
						</div>
					)
					: (
						<ul>
							{conversations.map((c) => (
								<li
									key={c.id}
									className="group flex items-center border-b border-border/50 last:border-0"
								>
									<Button
										type="button"
										variant="ghost"
										onClick={() => onSelect(c.id, { title: c.title })}
										className="flex-1 h-auto justify-start rounded-none px-4 py-2.5 hover:bg-muted transition-colors min-w-0"
									>
										<div className="flex items-center justify-between gap-2 w-full min-w-0">
											<p className="text-sm font-medium truncate">{c.title}</p>
											<p className="text-xs text-muted-foreground shrink-0 whitespace-nowrap">
												{formatDistanceToNow(new Date(c.updatedAt), { addSuffix: true })}
											</p>
										</div>
									</Button>
									<Button
										type="button"
										variant="ghost"
										size="icon"
										className="h-7 w-7 mr-2 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
										onClick={(e) => {
											e.stopPropagation()
											setPendingDeleteId(c.id)
										}}
										title={t('jobibot.deleteConversation')}
									>
										<Trash2 className="h-3.5 w-3.5" />
									</Button>
								</li>
							))}
						</ul>
					)}
			</div>

			<Dialog
				open={pendingDeleteId !== null}
				onOpenChange={(open) => {
					if (!open) { setPendingDeleteId(null) }
				}}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('jobibot.deleteConversationTitle')}</DialogTitle>
						<DialogDescription>
							{t('jobibot.deleteConversationDescription')}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<DialogClose asChild>
							<Button variant="outline" disabled={isDeleting}>{t('common.cancel')}</Button>
						</DialogClose>
						<Button
							variant="destructive"
							disabled={isDeleting}
							onClick={() => {
								if (!pendingDeleteId) { return }
								logger.track('Jobibot: Conversation deleted')
								deleteConversation({ conversationId: pendingDeleteId })
							}}
						>
							{isDeleting ? t('jobibot.deleting') : t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	)
}

function ChatPanel({
	conversationId,
	title,
	prefillText,
	isExpanded,
	onExpand,
	onBack,
	onClose,
	onPrefillSent,
}: {
	conversationId: string
	title?: string
	prefillText?: string
	isExpanded: boolean
	onExpand: () => void
	onBack: () => void
	onClose: () => void
	onPrefillSent?: () => void
}) {
	const { t } = useTranslation('common')
	const [input, setInput] = useState('')
	const messagesEndRef = useRef<HTMLDivElement>(null)
	const prefillSentRef = useRef(false)

	const currentFocus = useCurrentFocus()
	const [focusRemoved, setFocusRemoved] = useState(false)
	const focusKey = currentFocus ? `${currentFocus.entity_type}:${currentFocus.entity_id}` : null

	// A removed attachment stays removed until the page context changes.
	useEffect(() => {
		setFocusRemoved(false)
	}, [focusKey])

	const attachedFocus = focusRemoved ? null : currentFocus

	const {
		messages,
		sendMessage,
		stop,
		isProcessing,
		isLoading,
		isLoadingHistory,
		errorMessage,
	} = useGlobalChat(conversationId, Boolean(prefillText), attachedFocus)

	useEffect(() => {
		if (!prefillText || prefillSentRef.current || isLoadingHistory) { return }
		prefillSentRef.current = true
		void sendMessage(prefillText).then(() => onPrefillSent?.())
	}, [prefillText, isLoadingHistory, sendMessage, onPrefillSent])

	useEffect(() => {
		setTimeout(() => {
			messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
		}, 150)
	}, [messages])

	const handleSubmit = useCallback(async () => {
		const text = input.trim()
		if (!text || isProcessing) {
			return
		}
		setInput('')
		await sendMessage(text)
	}, [input, isProcessing, sendMessage])

	const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault()
			void handleSubmit()
		}
	}, [handleSubmit])

	return (
		<>
			<div className="flex items-center justify-between px-3 py-3 border-b border-border shrink-0">
				<div className="flex items-center gap-1">
					<Button variant="ghost" size="icon" className="h-7 w-7" onClick={onBack}>
						<ArrowLeft className="h-4 w-4" />
					</Button>
					<Jobibot size={18} />
					{title && <p className="text-xs font-medium truncate leading-tight max-w-[160px] ml-0.5">{title}
					</p>}
				</div>
				<div className="flex items-center gap-1">
					<Button
						variant="ghost"
						size="icon"
						className="h-7 w-7"
						onClick={onExpand}
						title={isExpanded ? t('jobibot.shrink') : t('jobibot.expand')}
					>
						{isExpanded ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
					</Button>
					<Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}>
						<X className="h-4 w-4" />
					</Button>
				</div>
			</div>

			<div className="flex-1 overflow-y-auto p-3 space-y-3 min-h-0">
				{isLoadingHistory
					? <MessageSkeleton />
					: messages.length === 0
					? (
						<p className="text-xs text-muted-foreground text-center py-8">
							{t('jobibot.emptyPrompt')}
						</p>
					)
					: messages.map((message) => <ChatBubble key={message.id} message={message} />)}

				{isLoading && (
					<div className="flex items-center gap-2 text-sm text-muted-foreground">
						<Loader />
						<Shimmer>{t('jobibot.thinking')}</Shimmer>
					</div>
				)}

				{errorMessage && (
					<p className="text-xs text-destructive bg-destructive/10 rounded-md px-2 py-1.5">
						{errorMessage}
					</p>
				)}

				<div ref={messagesEndRef} />
			</div>

			<div className="border-t border-border p-3 shrink-0">
				{attachedFocus && <FocusAttachment focus={attachedFocus} onRemove={() => setFocusRemoved(true)} />}
				<div className="flex gap-2 items-end">
					<Textarea
						value={input}
						onChange={(e) => setInput(e.target.value)}
						onKeyDown={handleKeyDown}
						placeholder={t('jobibot.inputPlaceholder')}
						disabled={isProcessing}
						rows={1}
						className="resize-none min-h-[36px] max-h-[120px] text-sm"
					/>
					{isProcessing
						? (
							<Button size="sm" variant="outline" onClick={stop} className="shrink-0">
								{t('jobibot.stop')}
							</Button>
						)
						: (
							<Button
								size="sm"
								onClick={() => void handleSubmit()}
								disabled={!input.trim()}
								className="shrink-0 bg-linear-to-br from-indigo-700 via-indigo-600 via-40% to-brand-600 text-white border-0"
							>
								{t('common.send')}
							</Button>
						)}
				</div>
				<p className="text-[10px] text-muted-foreground/60 text-center mt-1.5">
					{t('jobibot.disclaimer')}
				</p>
			</div>
		</>
	)
}

// ─── Types ───────────────────────────────────────────────────────────────────

type PersonResult = {
	id: string
	name: string | null
	primary_email: string | null
	primary_phone: string | null
	profile_url: string | null
	skills: string[]
	candidacy_count: number
}

type CampaignResult = {
	id: string
	title: string
	status: string | null
	location: string | null
	language: string | null
	requirements: string | null
	url: string | null
}

// Tool output is whatever the agent's tool serialised — JSON with no schema the
// spec describes, so the caller names the shape it expects.
function parseOutput<T>(output: unknown): T | null {
	if (typeof output === 'string') {
		try {
			return JSON.parse(output) as T
		} catch {
			return null
		}
	}
	return (output ?? null) as T | null
}

// ─── ChatBubble ───────────────────────────────────────────────────────────────

function ChatBubble({ message }: { message: UIMessage }) {
	const text = message.parts
		.flatMap((part) => isTextUIPart(part) ? [part.text] : [])
		.join('')

	const toolParts = message.parts
		.filter(isStaticToolUIPart)
		.filter((part) => part.output != null)

	if (!text && toolParts.length === 0) {
		return null
	}

	if (message.role === 'user') {
		return (
			<div className="flex justify-end">
				<div className="bg-primary text-primary-foreground text-sm rounded-xl rounded-tr-sm px-3 py-2 max-w-[85%] break-words">
					{text}
				</div>
			</div>
		)
	}

	const allPersonResults = toolParts
		.filter((part) => getToolName(part) === 'searchTalentPool')
		.flatMap((part) => parseOutput<{ results?: PersonResult[] }>(part.output)?.results ?? [])

	const personResults = allPersonResults.length <= 2 ? allPersonResults : []

	const campaignResult = toolParts
		.filter((part) => getToolName(part) === 'getCampaignDetails')
		.map((part) => parseOutput<CampaignResult>(part.output))
		.find((r) => r != null && !('error' in r) && Boolean(r.title)) ?? null

	const hasCards = personResults.length > 0 || campaignResult != null

	return (
		<div className="flex gap-2 items-start mr-[5%]">
			<div className="shrink-0 mt-1">
				<Jobibot size={18} />
			</div>
			<div className="flex flex-col gap-1.5 min-w-0 flex-1">
				{text && (
					<div className="bg-muted text-sm rounded-xl rounded-tl-sm px-3 py-2 break-words">
						<SecureMarkdown>{text}</SecureMarkdown>
					</div>
				)}
				{hasCards && (
					<AnimateChangeInHeight duration={0.15}>
						<div
							className={cn('flex flex-col gap-2', text && 'border-l-2 border-muted-foreground/20 pl-2')}
						>
							{personResults.map((person) => <PersonCard key={person.id} person={person} />)}
							{campaignResult && <CampaignCard campaign={campaignResult} />}
						</div>
					</AnimateChangeInHeight>
				)}
			</div>
		</div>
	)
}

// ─── Entity cards ─────────────────────────────────────────────────────────────

function PersonCard({ person }: { person: PersonResult }) {
	const visibleSkills = person.skills.slice(0, 4)
	const extraSkills = person.skills.length - visibleSkills.length

	return (
		<div className="rounded-lg border border-border bg-background p-3 text-sm space-y-2">
			<div className="flex items-start justify-between gap-2">
				<div className="min-w-0">
					<p className="font-medium truncate">{person.name ?? 'Unknown'}</p>
					{person.primary_email && (
						<p className="text-xs text-muted-foreground truncate">{person.primary_email}</p>
					)}
					{person.primary_phone && <p className="text-xs text-muted-foreground">{person.primary_phone}</p>}
				</div>
				{person.profile_url && (
					<Link
						to={person.profile_url as any}
						className="shrink-0 flex items-center gap-1 text-xs text-primary hover:underline"
					>
						View <ExternalLink className="h-3 w-3" />
					</Link>
				)}
			</div>
			{visibleSkills.length > 0 && (
				<div className="flex flex-wrap gap-1">
					{visibleSkills.map((skill) => (
						<span
							key={skill}
							className="inline-block bg-muted text-muted-foreground text-[10px] px-1.5 py-0.5 rounded-full"
						>
							{skill}
						</span>
					))}
					{extraSkills > 0 && (
						<span className="inline-block text-[10px] text-muted-foreground px-1">
							+{extraSkills} more
						</span>
					)}
				</div>
			)}
			{person.candidacy_count > 0 && (
				<p className="text-[10px] text-muted-foreground">
					{person.candidacy_count} application{person.candidacy_count !== 1 ? 's' : ''}
				</p>
			)}
		</div>
	)
}

function CampaignCard({ campaign }: { campaign: CampaignResult }) {
	return (
		<div className="rounded-lg border border-border bg-background p-3 text-sm space-y-1">
			<div className="flex items-start justify-between gap-2">
				<p className="font-medium">{campaign.title}</p>
				{campaign.url && (
					<Link
						to={campaign.url as any}
						className="shrink-0 flex items-center gap-1 text-xs text-primary hover:underline"
					>
						View <ExternalLink className="h-3 w-3" />
					</Link>
				)}
			</div>
			<div className="flex gap-2 text-xs text-muted-foreground">
				{campaign.status && (
					<span className="capitalize bg-muted px-1.5 py-0.5 rounded-full">{campaign.status}</span>
				)}
				{campaign.location && <span>{campaign.location}</span>}
				{campaign.language && <span>{campaign.language.toUpperCase()}</span>}
			</div>
			{campaign.requirements && (
				<p className="text-xs text-muted-foreground line-clamp-2">{campaign.requirements}</p>
			)}
		</div>
	)
}

function MessageSkeleton() {
	return (
		<div className="flex flex-col gap-4 p-1 animate-pulse">
			<div className="flex gap-2 items-start mr-[20%]">
				<div className="size-5 rounded-full bg-muted shrink-0 mt-0.5" />
				<div className="flex flex-col gap-1.5 flex-1">
					<div className="h-3 bg-muted rounded w-3/4" />
					<div className="h-3 bg-muted rounded w-1/2" />
				</div>
			</div>
			<div className="flex justify-end ml-[20%]">
				<div className="h-8 bg-muted rounded-xl w-2/3" />
			</div>
			<div className="flex gap-2 items-start mr-[20%]">
				<div className="size-5 rounded-full bg-muted shrink-0 mt-0.5" />
				<div className="flex flex-col gap-1.5 flex-1">
					<div className="h-3 bg-muted rounded w-full" />
					<div className="h-3 bg-muted rounded w-4/5" />
					<div className="h-3 bg-muted rounded w-2/3" />
				</div>
			</div>
		</div>
	)
}
